/**
 * LuxuryAvatar — premium Instagram-grade fashion illustration component.
 *
 * Replaces the procedural SVG croquis with curated AI-generated luxury
 * illustrations (one per gender × category) cached as static PNGs at
 * /avatars/<gender>_<schemaKey>.png. Wraps each illustration with:
 *   · Confidence-driven warm halo (opacity scales with confidence 0–100)
 *   · Breathing micro-animation (subtle scale, no jitter)
 *   · Soft floating motion (translateY)
 *   · Fit-reaction width transform (slim → 0.97 / regular → 1.0 / loose → 1.04)
 *   · Crossfade between fit variants and category swaps
 *   · Decorative gold hairline frame in the Sabyasachi editorial palette
 *
 * Lightweight: 1 <img>, CSS keyframes only, no JS per frame.
 */
import React, { useMemo, useState, useEffect } from 'react';

const GOLD = '#D4AF37';

// Category → schema → filename. We only have one illustration per
// (gender × schemaKey); fit variations are conveyed by transform/crossfade.
const AVATAR_MAP = {
  female: {
    blouse: 'female_blouse.png',
    suit: 'female_suit.png',
    kurta_pajama: 'female_kurta_pajama.png',
    men_shirt: 'female_blouse.png',          // fallback
    trouser: 'female_trouser.png',
    sherwani: 'female_suit.png',             // fallback (long structured silhouette)
  },
  male: {
    blouse: 'male_men_shirt.png',            // fallback (top-only)
    suit: 'male_kurta_pajama.png',           // fallback
    kurta_pajama: 'male_kurta_pajama.png',
    men_shirt: 'male_men_shirt.png',
    trouser: 'male_trouser.png',
    sherwani: 'male_sherwani.png',
  },
};

const resolveAvatar = (gender, schemaKey) => {
  const g = (gender === 'male') ? 'male' : 'female';
  const map = AVATAR_MAP[g];
  return `/avatars/${map[schemaKey] || map.blouse}`;
};

// Fit reaction: tightens/relaxes garment silhouette via subtle X-axis transform.
const FIT_SCALE = {
  slim:    { sx: 0.965, sy: 1.005, label: 'Slim' },
  regular: { sx: 1.000, sy: 1.000, label: 'Regular' },
  loose:   { sx: 1.040, sy: 0.998, label: 'Loose' },
};

const LuxuryAvatar = ({
  gender = 'female',
  schemaKey = 'blouse',
  fitPref = 'regular',
  confidence = 80,    // 0-100, drives halo intensity
  size = 280,         // rendered width (px). Aspect ratio kept at 3:4.
}) => {
  const src = useMemo(() => resolveAvatar(gender, schemaKey), [gender, schemaKey]);
  const fit = FIT_SCALE[fitPref] || FIT_SCALE.regular;

  // Confidence halo opacity: 0.18 (low) → 0.62 (high)
  const conf = Math.max(0, Math.min(100, confidence)) / 100;
  const haloOpacity = 0.18 + conf * 0.44;

  // Crossfade key — re-mounts the <img> when src changes for a smooth fade.
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(false); }, [src]);

  const uid = useMemo(() => `lx-${Math.random().toString(36).slice(2, 8)}`, []);

  return (
    <div
      data-testid="luxury-avatar"
      className="relative inline-block overflow-hidden"
      style={{
        width: size,
        aspectRatio: '3 / 4',
        background: 'linear-gradient(180deg, #FBF6EC 0%, #F2E8D6 100%)',
        border: `1px solid rgba(212,175,55,0.35)`,
        boxShadow: '0 12px 36px rgba(60,40,20,0.10), 0 2px 6px rgba(60,40,20,0.06)',
      }}>

      {/* Inner gold hairline frame (editorial chrome) */}
      <div className="pointer-events-none absolute inset-2"
        style={{ border: `1px solid rgba(212,175,55,0.30)` }} />

      {/* Confidence halo — warm radial glow behind the model */}
      <div
        data-testid="luxury-avatar-halo"
        className={`${uid}-halo absolute inset-0 pointer-events-none`}
        style={{
          background: `radial-gradient(ellipse at 50% 45%, rgba(245,206,140,${haloOpacity}) 0%, rgba(245,206,140,0) 65%)`,
          opacity: 1,
        }} />

      {/* The illustration itself — breathing wrap with fit-reactive transform */}
      <div className={`${uid}-breathe absolute inset-0 flex items-end justify-center`}>
        <img
          src={src}
          alt={`${gender} ${schemaKey} luxury fit preview`}
          onLoad={() => setLoaded(true)}
          data-testid="luxury-avatar-img"
          className={`${uid}-fade`}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 18%',
            transform: `scaleX(${fit.sx}) scaleY(${fit.sy})`,
            transformOrigin: '50% 70%',
            transition: 'transform 600ms cubic-bezier(0.4,0,0.2,1), opacity 700ms ease-out',
            opacity: loaded ? 1 : 0,
            willChange: 'transform, opacity',
          }} />
      </div>

      {/* Top-right fit pill — luxury chip showing current silhouette */}
      <div className="absolute top-3 right-3 px-2.5 py-1 flex items-center gap-1.5"
        data-testid="luxury-avatar-fit-pill"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(10px)',
          border: `1px solid rgba(212,175,55,0.45)`,
          boxShadow: '0 4px 14px rgba(60,40,20,0.10)',
        }}>
        <span className="block w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />
        <span className="text-[9px] tracking-[0.18em] uppercase"
          style={{ color: '#1A1306', fontWeight: 600 }}>
          {fit.label} Fit
        </span>
      </div>

      {/* Bottom-left confidence chip */}
      <div className="absolute bottom-3 left-3 px-2.5 py-1.5"
        data-testid="luxury-avatar-conf-chip"
        style={{
          background: 'rgba(255,255,255,0.86)',
          backdropFilter: 'blur(10px)',
          border: `1px solid rgba(212,175,55,0.45)`,
          boxShadow: '0 4px 14px rgba(60,40,20,0.10)',
        }}>
        <p className="text-[8px] tracking-[0.22em] uppercase mb-0" style={{ color: '#7A6A4D' }}>
          AI Confidence
        </p>
        <p className="text-[14px] leading-none mt-0.5"
          style={{ fontFamily: 'Playfair Display, serif', color: GOLD, fontWeight: 600 }}>
          {Math.round(confidence)}%
        </p>
      </div>

      {/* Skeleton shimmer while image loads */}
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center"
          style={{ background: 'linear-gradient(180deg, #FBF6EC 0%, #F2E8D6 100%)' }}>
          <div className={`${uid}-shimmer w-3/5 h-3/4`}
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.18), transparent)',
              backgroundSize: '200% 100%',
            }} />
        </div>
      )}

      <style>{`
        @keyframes ${uid}-breathe {
          0%, 100% { transform: translateY(0) scale(1);     }
          50%      { transform: translateY(-4px) scale(1.012); }
        }
        .${uid}-breathe { animation: ${uid}-breathe 5.6s ease-in-out infinite; }

        @keyframes ${uid}-halo-pulse {
          0%, 100% { transform: scale(1);    opacity: 0.92; }
          50%      { transform: scale(1.06); opacity: 1;    }
        }
        .${uid}-halo { animation: ${uid}-halo-pulse 4.2s ease-in-out infinite; transform-origin: 50% 45%; }

        @keyframes ${uid}-shimmer {
          0%, 100% { background-position: 200% 0; }
          50%      { background-position: -200% 0; }
        }
        .${uid}-shimmer { animation: ${uid}-shimmer 1.6s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default LuxuryAvatar;
